import { formatRate, type RateUnit } from '@/core/display-settings'
import { widgetCopy } from '@/i18n/widget-copy'
import type { AppLocale } from '@/i18n/messages'
import { streamMessages } from '@/i18n/stream'
import type { ExpRateTrendPresentation } from './exp-rate-trend'
import type { ExpReadoutPresentation } from './exp-readout-presentation'
import type { ExpRateChartSnapshot } from '@/domain/types'
import { expChartSample } from './exp-chart-sample'
import { elapsedLayoutWidth, fitWidgetElapsed } from './widget-elapsed'
import { allowsWidgetEllipsis } from './widget-overflow'

export interface StreamHudData {
  expChart?: ExpRateChartSnapshot
  rateUnit?: RateUnit
  sessionId: string | null
  status: string
  statusText: string
  elapsed: string
  level: string
  jobName?: string
  expRate: string
  expReadout?: ExpReadoutPresentation
  /** Raw displayed mean, including the last confirmed value during brief checks. */
  expDisplayRateValue?: number | null
  expRateValue?: number | null
  expRateTrend?: ExpRateTrendPresentation
  expPercent: string
  expRatio: number
  eta: string
  expGained: string
  expGainedLabel: string
  fullDuration: string
  mesosRate: string
  mesosNet: string
  mesosStart: string
  mesosBalanceChange: string
  hpPercent: string
  hpRatio: number
  mpPercent: string
  mpRatio: number
  map: string
  enabled: { mesos: boolean; hp: boolean; mp: boolean; mapName: boolean }
  levelUpId?: string
}

export const STREAM_ASSETS = Object.freeze({
  exp: '/stream-assets/exp.png', mesos: '/stream-assets/mesos.png',
  hp: '/stream-assets/hp.png', mp: '/stream-assets/mp.png', mushroom: '/stream-assets/mushroom.png',
})

export type StreamShape = 'classic' | 'strip' | 'portrait'

/** Only text/visibility with a layout footprint; trend animation owns a fixed box. */
export function streamLayoutContent(data: StreamHudData): string {
  return JSON.stringify([data.rateUnit, elapsedLayoutWidth(data.elapsed), data.level, data.jobName, data.expRate,
    data.expPercent, data.eta, data.expGained, data.expGainedLabel, data.fullDuration,
    data.mesosRate, data.mesosStart, data.mesosBalanceChange, data.hpPercent, data.mpPercent,
    data.map, data.enabled])
}
export function resolveStreamShape(width: number, height: number): StreamShape {
  if (width >= 320 && width / height >= 2.5) return 'strip'
  if (height / width >= 1.5) return 'portrait'
  return 'classic'
}

/** Measure text as well as boxes: fixed grids can conceal clipped unit suffixes. */
export function streamContentFits(root: HTMLElement): boolean {
  const bounds = root.getBoundingClientRect()
  if (!bounds.width || !bounds.height) return true
  const range = root.ownerDocument.createRange()
  for (const node of root.querySelectorAll<HTMLElement>('[data-stream-measure], .widget-player, .widget-player *')) {
    if (node.closest('.exp-readout-dialog')) continue
    if (node.closest('[data-stream-motion], .exp-rate-trend-layer, .exp-chart')) continue
    if (!node.getClientRects().length) continue
    const box = node.getBoundingClientRect()
    if (box.left < bounds.left - 1 || box.right > bounds.right + 1 || box.top < bounds.top - 1 || box.bottom > bounds.bottom + 1) return false
    if (node.classList.contains('exp-rate-trend') || node.classList.contains('sh-rate-measure')) continue
    // Artwork owns a static footprint; moving sprites must not change detail tiers.
    if (node.hasAttribute('data-stream-artwork')) continue
    // Ancestors also inherit sprite overflow; their text is checked in its own boxes.
    const ellipsized = allowsWidgetEllipsis(node)
    if (!ellipsized && !node.querySelector('[data-stream-artwork]') && node.clientWidth && node.scrollWidth > node.clientWidth + 1) return false
    for (const child of node.childNodes) {
      if (child.nodeType !== 3 || !child.textContent?.trim()) continue
      range.selectNodeContents(child)
      const text = range.getBoundingClientRect()
      const textBounds = node.closest('.widget-player') ? box : bounds
      if ((!ellipsized && (text.left < textBounds.left - 1 || text.right > textBounds.right + 1)) || text.top < bounds.top - 1 || text.bottom > bounds.bottom + 1) return false
    }
  }
  return true
}

export function fitStreamDetail(root: HTMLElement, fits = streamContentFits, preview = false,
  limit?: { detail: number; artworkHidden: boolean }): number {
  // Restore the theme before measuring, including when a larger window returns.
  root.dataset.artwork = limit?.artworkHidden ? 'hidden' : 'visible'
  if (root.querySelector('.compact-exp')) {
    root.dataset.detail = '0'
    if (!fitWidgetElapsed(root, fits, true)) root.dataset.elapsedMode = 'hidden'
    return 0
  }
  for (let tier = Math.min(5, limit?.detail ?? 5); tier >= 2; tier--) {
    root.dataset.detail = String(tier)
    if (fitWidgetElapsed(root, fits, tier < 3)) return tier
  }
  // Preview cards showcase the theme: scale the core composition instead of removing it.
  if (preview) return 2
  // Try core data with its companion first; remove artwork before core metrics.
  root.dataset.artwork = 'hidden'
  for (let tier = Math.min(2, limit?.detail ?? 2); tier >= 0; tier--) {
    root.dataset.detail = String(tier)
    if (fitWidgetElapsed(root, fits, tier < 3)) return tier
  }
  root.dataset.elapsedMode = 'hidden'
  return 0
}

export function streamPreviewData(locale: AppLocale, rateUnit: RateUnit = 'hour'): StreamHudData {
  const compact = (value: number) => new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
  return {
    expChart: expChartSample(), rateUnit, sessionId: 'preview', status: 'running', statusText: streamMessages[locale].recording, elapsed: '01:47:37', level: '75',
    jobName: { 'zh-CN': '祭司', 'zh-TW': '祭司', en: 'Priest', ja: 'プリースト', ko: '프리스트' }[locale],
    expRate: formatRate(304400, locale, rateUnit, { maximumFractionDigits: 1 }), expDisplayRateValue: 304400, expRateValue: 304400, expPercent: '44.15%', expRatio: .4415, eta: widgetCopy(locale, '4h 57m', '4小时57分'),
    expGained: `+${compact(68200)}`, expGainedLabel: streamMessages[locale].gained, fullDuration: widgetCopy(locale, '8h 51m', '8小时51分'),
    mesosRate: formatRate(213200, locale, rateUnit, { maximumFractionDigits: 1 }), mesosNet: `+${compact(47600)}`, mesosStart: compact(4336560), mesosBalanceChange: `+${compact(47600)}`,
    hpPercent: '53%', hpRatio: .53, mpPercent: '49%', mpRatio: .49,
    map: widgetCopy(locale, 'Victoria Island / Forest of Wisdom', '金银岛 / 智慧森林'), enabled: { mesos: true, hp: true, mp: true, mapName: true },
  }
}
